import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { Button } from "@/components/ui/button";

import { hayCambiosSinGuardar, vigilarLoEscrito } from "./cambios-sin-guardar";

/** El aviso que manda `sw-activate.js` al activar una versión: quien lo contesta decide si recarga. */
const AVISO_VERSION = "neca:version-nueva";

/** Si esta página ya corre la versión publicada: el `index.html` de la red pide su mismo programa. */
async function yaEsLaUltima(): Promise<boolean> {
  const propio = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute("src");
  if (!propio) return false;
  try {
    const respuesta = await fetch("/", { cache: "no-store", credentials: "same-origin" });
    return respuesta.ok && (await respuesta.text()).includes(propio);
  } catch {
    return false; // sin red no se sabe: se sigue como siempre
  }
}

/**
 * **Las versiones nuevas de la aplicación** (D-062, owner 15-oct #0b). Se consulta al registrar, al volver
 * a la pestaña, al reconectar y cada quince segundos. Cuando se activa una versión nueva, la página se
 * recarga sola si no hay nada sin guardar (`cambios-sin-guardar.ts`); si lo hay, avisa «Hay una versión nueva · Actualizar» y espera
 * el clic. `sw-activate.js` pregunta a cada pestaña y solo recarga las que no contestan (las de una
 * versión anterior, sin este aviso).
 *
 * Ola de limpieza (19-oct): la página abierta DESPUÉS de publicar ya corre la versión nueva (el HTML viene
 * siempre de la red); lo viejo era solo el service worker, que se pone al día unos segundos después de entrar.
 * Recargarla entonces no traía nada y era la «recarga sola a los 10 s» de cada sesión nueva: ahora no recarga
 * si el `index.html` de la red pide el mismo programa que esta página.
 */
export function PwaUpdates({ recargar = () => window.location.reload() }: { recargar?: () => void }) {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const [aviso, setAviso] = useState(false);
  const nuevaVersion = async () => {
    if (await yaEsLaUltima()) return;
    if (hayCambiosSinGuardar()) setAviso(true);
    else recargar();
  };
  useRegisterSW({
    immediate: true,
    onRegisteredSW(_url, registered) { setRegistration(registered); },
    onNeedReload: nuevaVersion,
  });

  useEffect(() => {
    const dejarDeVigilar = vigilarLoEscrito();
    // Contestar el aviso del SW: esta pestaña decide (la recarga la pide `onNeedReload`).
    const sw = navigator.serviceWorker;
    const contestar = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === AVISO_VERSION) e.ports[0]?.postMessage("me encargo");
    };
    sw?.addEventListener("message", contestar);
    sw?.startMessages();
    return () => {
      dejarDeVigilar();
      sw?.removeEventListener("message", contestar);
    };
  }, []);

  useEffect(() => {
    if (!registration) return;
    let checking = false;
    const check = () => {
      if (checking || !navigator.onLine || document.visibilityState !== "visible") return;
      checking = true;
      void registration.update().catch(() => {
        // Sin red se conserva la versión actual hasta el siguiente foco o intervalo.
      }).finally(() => { checking = false; });
    };
    check();
    const timer = window.setInterval(check, 15 * 1000);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [registration]);

  if (!aviso) return null;
  // El mismo aviso de abajo que `Toast`, pero no se va solo: espera el clic.
  return (
    <div role="status" className="fixed bottom-6 left-1/2 z-50 flex max-w-[min(640px,92vw)] -translate-x-1/2 items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
      <span className="flex-1">Hay una versión nueva. Guarda lo que estés haciendo y actualiza.</span>
      <Button type="button" onClick={recargar}><RefreshCw aria-hidden /> Actualizar</Button>
    </div>
  );
}
